import { createTheme, Theme } from '@mui/material'
import { LIGHT, DARK, ThemeMode } from './theme/tokens'

declare module '@mui/material/styles' {
  interface Theme {
    status: {
      white: React.CSSProperties['color']
      base: React.CSSProperties['color']
    }
  }

  interface Palette {
    white: Palette['primary']
    base: Palette['primary']
  }
  interface PaletteOptions {
    white: PaletteOptions['primary']
    base: PaletteOptions['primary']
  }

  interface PaletteColor {
    darker?: string
  }
  interface SimplePaletteColorOptions {
    dark?: string
    light?: string
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    base: true
  }
}

// MUI themes are GENERATED ARTIFACTS of src/theme/tokens.ts — never a second
// source of truth. Real hex is required here: MUI 5's color math (alpha(),
// augmentColor) throws on var() strings. palette.mode does the heavy lifting
// for stock components (menus, dialogs, tables, alerts) in dark.
export const buildAppTheme = (mode: ThemeMode): Theme => {
  const t = mode === 'dark' ? DARK : LIGHT
  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#A58450',
        light: '#B79C73',
        dark: '#735C38',
      },
      success: {
        main: '#3CD670',
        light: '#79ffa0',
        dark: '#00a342',
        contrastText: '#fff',
      },
      white: {
        main: '#FFFFFF',
        light: '#FFFFFF',
        dark: '#FFFFFF',
      },
      base: {
        main: '#000000',
        light: '#000000',
        dark: '#000000',
      },
      background: {
        default: t.page,
        paper: t.surface,
      },
      text: {
        primary: t.text,
        secondary: t.text2,
        disabled: t.muted,
      },
      divider: t.border,
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: 'CircularStd, Helvetica, Arial, sans-serif',
      h1: {
        fontFamily: 'CircularStdBlack, Helvetica, Arial, sans-serif',
      },
      h2: {
        fontFamily: 'CircularStdBlack, Helvetica, Arial, sans-serif',
      },
      h3: {
        fontFamily: 'CircularStdBlack, Helvetica, Arial, sans-serif',
      },
      h4: {
        fontFamily: 'CircularStdBlack, Helvetica, Arial, sans-serif',
      },
      h5: {
        fontFamily: 'CircularStdBlack, Helvetica, Arial, sans-serif',
      },
      h6: {
        fontFamily: 'CircularStd, Helvetica, Arial, sans-serif',
      },
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            '&.Mui-selected': {
              fontWeight: 700,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 6,
          },
        },
      },
      ...(mode === 'dark'
        ? {
            // Kill MUI's dark-mode elevation overlay so Paper reads as the
            // flat --surface the spec defines.
            MuiPaper: {
              styleOverrides: {
                root: {
                  backgroundImage: 'none',
                },
              },
            },
            // MUI hardcodes a light grey for a default (letter/icon) Avatar and
            // for an unchecked Switch track; both read as bright blobs on the
            // dark canvas. Light mode keeps MUI's defaults untouched.
            MuiAvatar: {
              styleOverrides: {
                colorDefault: {
                  backgroundColor: DARK.surface2,
                  color: DARK.text2,
                },
              },
            },
            MuiSwitch: {
              styleOverrides: {
                track: {
                  backgroundColor: DARK.muted,
                },
              },
            },
          }
        : {}),
    },
  })
}

// Light theme kept under its historical name: RmnThemeProvider (and any other
// direct importer) builds on the light theme regardless of portal mode.
export const theme = buildAppTheme('light')
